import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Separator } from 'src/@/components/ui/separator';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import propostasService from 'src/services/propostasService';
import {
  PROPOSTAS_CONFIG_DESIGN as D,
  dadosExemploPreview,
  resolverPreview,
  GRUPOS_VARIAVEIS,
  VARIAVEIS_FALLBACK,
} from './PropostasConfig.design';

const CORES = ['#6b7280', '#3b82f6', '#f59e0b', '#22c55e', '#ef4444', '#f97316', '#7C3AED', '#06b6d4', '#ec4899'];

export default function PropostasConfigComponent({ cliente }) {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { toast } = useToast();

  // Status do ciclo de vida da proposta que um estágio do kanban pode representar.
  const STATUS_DISPONIVEIS = [
    { value: 'rascunho', label: t('proposalSettings.stageStatus.rascunho.label', 'Rascunho'), descricao: t('proposalSettings.stageStatus.rascunho.description', 'Ainda não enviada') },
    { value: 'enviada', label: t('proposalSettings.stageStatus.enviada.label', 'Enviada'), descricao: t('proposalSettings.stageStatus.enviada.description', 'Enviada ao cliente') },
    { value: 'visualizada', label: t('proposalSettings.stageStatus.visualizada.label', 'Visualizada'), descricao: t('proposalSettings.stageStatus.visualizada.description', 'Cliente abriu o link') },
    { value: 'aceita', label: t('proposalSettings.stageStatus.aceita.label', 'Aceita'), descricao: t('proposalSettings.stageStatus.aceita.description', 'Cliente aceitou') },
    { value: 'recusada', label: t('proposalSettings.stageStatus.recusada.label', 'Recusada'), descricao: t('proposalSettings.stageStatus.recusada.description', 'Cliente recusou') },
    { value: 'expirada', label: t('proposalSettings.stageStatus.expirada.label', 'Expirada'), descricao: t('proposalSettings.stageStatus.expirada.description', 'Passou da validade') },
    { value: 'cancelada', label: t('proposalSettings.stageStatus.cancelada.label', 'Cancelada'), descricao: t('proposalSettings.stageStatus.cancelada.description', 'Cancelada internamente') },
  ];

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [variaveis, setVariaveis] = useState(() => VARIAVEIS_FALLBACK(t));

  // Campo do e-mail que recebe a variável ao clicar num chip da paleta.
  const [campoAtivo, setCampoAtivo] = useState('corpo');
  const assuntoRef = useRef(null);
  const corpoRef = useRef(null);

  useEffect(() => {
    if (user?.clienteId) loadConfig();
  }, [user?.clienteId]);

  useEffect(() => {
    // Catálogo de variáveis vem do backend para não divergir do resolvedor.
    // {{itens.tabela}} só é substituído na renderização da proposta (página
    // pública/PDF), nunca no e-mail — fica fora da paleta.
    propostasService.variaveis()
      .then(res => {
        if (!res?.error && Array.isArray(res.data)) {
          setVariaveis(res.data.filter(v => v.chave !== 'itens.tabela'));
        }
      })
      .catch(() => { /* mantém VARIAVEIS_FALLBACK */ });
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await propostasService.buscarConfig(user?.clienteId);
      if (!response.error) setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configuração de propostas:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateEstagio = (index, patch) => {
    setConfig(prev => ({
      ...prev,
      estagios: prev.estagios.map((e, i) => i === index ? { ...e, ...patch } : e)
    }));
  };

  const moveEstagio = (index, dir) => {
    const j = index + dir;
    setConfig(prev => {
      const lista = [...(prev.estagios || [])];
      if (j < 0 || j >= lista.length) return prev;
      [lista[index], lista[j]] = [lista[j], lista[index]];
      return { ...prev, estagios: lista.map((e, i) => ({ ...e, ordem: i })) };
    });
  };

  const removeEstagio = (index) => {
    setConfig(prev => ({
      ...prev,
      estagios: prev.estagios.filter((_, i) => i !== index).map((e, i) => ({ ...e, ordem: i }))
    }));
  };

  const addEstagio = () => {
    const usados = (config.estagios || []).map(e => e.status);
    const disponivel = STATUS_DISPONIVEIS.find(s => !usados.includes(s.value));
    if (!disponivel) {
      toast({ title: t('status.warning', { ns: 'common' }), description: t('proposalSettings.stages.allStatusUsedMessage', 'Todos os status já possuem um estágio'), variant: 'destructive' });
      return;
    }
    setConfig(prev => ({
      ...prev,
      estagios: [...(prev.estagios || []), { nome: disponivel.label, cor: '#6b7280', status: disponivel.value, ordem: (prev.estagios || []).length }]
    }));
  };

  const setEmail = (patch) => setConfig(prev => ({ ...prev, email: { ...prev.email, ...patch } }));

  const assunto = config?.email?.assunto_padrao || '';
  const corpo = config?.email?.corpo_padrao || '';

  // Insere {{chave}} na posição do cursor do último campo focado (assunto ou
  // corpo) e devolve o foco com o caret depois do token.
  const inserirVariavel = (chave) => {
    const ehAssunto = campoAtivo === 'assunto';
    const el = ehAssunto ? assuntoRef.current : corpoRef.current;
    const atual = ehAssunto ? assunto : corpo;
    const token = `{{${chave}}}`;
    const inicio = el ? el.selectionStart : atual.length;
    const fim = el ? el.selectionEnd : atual.length;
    const novo = atual.slice(0, inicio) + token + atual.slice(fim);

    setEmail(ehAssunto ? { assunto_padrao: novo } : { corpo_padrao: novo });

    // O valor só chega ao DOM no próximo render — reposiciona o caret depois dele.
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = inicio + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // Rótulos dos grupos já traduzidos (ver GRUPOS_VARIAVEIS em PropostasConfig.design.js).
  const grupoLabels = useMemo(() => GRUPOS_VARIAVEIS(t), [t]);

  // Agrupa a paleta por prefixo da chave (proposta.*, contato.*, ...) na ordem
  // de GRUPOS_VARIAVEIS; grupos desconhecidos vão para o fim.
  const variaveisAgrupadas = useMemo(() => {
    const ordem = Object.keys(grupoLabels);
    const mapa = variaveis.reduce((acc, v) => {
      const grupo = v.chave.split('.')[0];
      (acc[grupo] = acc[grupo] || []).push(v);
      return acc;
    }, {});
    return Object.entries(mapa).sort(
      ([a], [b]) => (ordem.indexOf(a) + 1 || 99) - (ordem.indexOf(b) + 1 || 99)
    );
  }, [variaveis, grupoLabels]);

  const nomeEmpresa = cliente?.nome || cliente?.nomeFantasia || '';
  const dadosPreview = useMemo(() => dadosExemploPreview(nomeEmpresa, t), [nomeEmpresa, t]);
  const assuntoPreview = resolverPreview(assunto, dadosPreview) || '(sem assunto)';
  const corpoPreview = resolverPreview(corpo, dadosPreview);
  const empresaPreview = dadosPreview['empresa.nome'];
  const assuntoSemEmpresa = Boolean(assunto) && !assunto.includes('{{empresa.nome}}');

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await propostasService.salvarConfig({
        validade_dias_default: config.validade_dias_default,
        lembrete_dias_antes: config.lembrete_dias_antes,
        mensagem_whatsapp_padrao: config.mensagem_whatsapp_padrao,
        email: config.email,
        assinatura_default: config.assinatura_default,
        estagios: config.estagios
      });
      if (response.error) throw new Error(response.message);
      toast({ title: t('status.success', { ns: 'common' }), description: t('proposalSettings.toast.updateSuccess', 'Configuração de propostas atualizada') });
    } catch (error) {
      toast({ title: t('status.error', { ns: 'common' }), description: error.message || t('proposalSettings.toast.updateError', 'Não foi possível salvar'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-64">
        <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin text-[#7C3AED]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-violet-50 dark:bg-violet-500/10">
          <Iconify icon="mdi:file-document-outline" width={22} className="text-[#7C3AED]" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3]">{t('proposalSettings.header.title', 'Propostas')}</h2>
          <p className="text-sm text-gray-500 dark:text-[#ABABAD]">{t('proposalSettings.header.subtitle', 'Configurações gerais de propostas comerciais')}</p>
        </div>
      </div>

      {/* Estágios do kanban */}
      <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3]">{t('proposalSettings.stages.title', 'Estágios do kanban')}</h3>
            <p className="text-xs text-gray-500 dark:text-[#ABABAD] mt-0.5">
              {t('proposalSettings.stages.description', 'Colunas da visão kanban de propostas. Cada estágio representa uma etapa do ciclo de vida — os cards se movem sozinhos quando a proposta é enviada, aberta pelo cliente ou respondida.')}
            </p>
          </div>
        </div>

        <div className="space-y-2 mt-4">
          {(config.estagios || []).map((estagio, index) => (
            <div key={index} className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-100 dark:border-[#383838] bg-gray-50/50 dark:bg-[#1A1D21]/40">
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button
                  onClick={() => moveEstagio(index, -1)}
                  disabled={index === 0}
                  className="p-0.5 rounded text-gray-400 hover:text-[#7C3AED] disabled:opacity-30 transition-colors"
                >
                  <Iconify icon="mdi:chevron-up" width={14} />
                </button>
                <button
                  onClick={() => moveEstagio(index, 1)}
                  disabled={index === (config.estagios.length - 1)}
                  className="p-0.5 rounded text-gray-400 hover:text-[#7C3AED] disabled:opacity-30 transition-colors"
                >
                  <Iconify icon="mdi:chevron-down" width={14} />
                </button>
              </div>

              <div className="relative flex-shrink-0">
                <span
                  className="w-7 h-7 rounded-lg block border border-gray-200 dark:border-[#383838]"
                  style={{ backgroundColor: estagio.cor }}
                />
                <select
                  value={estagio.cor}
                  onChange={e => updateEstagio(index, { cor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  title={t('proposalSettings.stages.colorTitle', 'Cor do estágio')}
                >
                  {CORES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <Input
                value={estagio.nome || ''}
                onChange={e => updateEstagio(index, { nome: e.target.value })}
                placeholder={t('proposalSettings.stages.namePlaceholder', 'Nome do estágio')}
                className="h-9 text-sm rounded-lg flex-1 min-w-0"
              />

              <select
                value={estagio.status || ''}
                onChange={e => updateEstagio(index, { status: e.target.value })}
                className="h-9 text-xs rounded-lg border border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] text-gray-700 dark:text-[#D1D2D3] px-2 w-[150px] flex-shrink-0 focus:outline-none focus:border-[#7C3AED]"
                title={t('proposalSettings.stages.statusTitle', 'Etapa do ciclo de vida que este estágio representa')}
              >
                {STATUS_DISPONIVEIS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              <button
                onClick={() => removeEstagio(index)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
                title={t('proposalSettings.stages.removeTitle', 'Remover estágio')}
              >
                <Iconify icon="mdi:trash-can-outline" width={16} />
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={addEstagio}
          className="flex items-center justify-center gap-1.5 mt-3 w-full px-3 py-2 text-sm text-[#7C3AED] font-medium hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-lg border border-dashed border-violet-200 dark:border-violet-500/30 transition-colors"
        >
          <Iconify icon="eva:plus-fill" width={16} />
          {t('proposalSettings.stages.addButton', 'Adicionar estágio')}
        </button>

        <p className="text-[11px] text-gray-400 mt-2">
          {t('proposalSettings.stages.footerNote', 'Propostas cujo status não tenha um estágio correspondente não aparecem no kanban — elas continuam visíveis na visão em lista.')}
        </p>
      </div>

      <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">{t('proposalSettings.general.validityLabel', 'Validade padrão (dias)')}</Label>
            <Input
              type="number"
              value={config.validade_dias_default}
              onChange={e => setConfig(prev => ({ ...prev, validade_dias_default: Number(e.target.value) }))}
              className="mt-1.5 rounded-xl border-gray-200 dark:border-[#383838]"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">{t('proposalSettings.general.reminderLabel', 'Lembrete antes de expirar (dias)')}</Label>
            <Input
              type="number"
              value={config.lembrete_dias_antes}
              onChange={e => setConfig(prev => ({ ...prev, lembrete_dias_antes: Number(e.target.value) }))}
              className="mt-1.5 rounded-xl border-gray-200 dark:border-[#383838]"
            />
          </div>
        </div>

        <Separator className="bg-gray-200/60 dark:bg-[#383838]/60" />

        {/* Assinatura: vale para propostas novas — cada proposta ainda pode
            ligar/desligar a exigência no editor. */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Iconify icon="mdi:draw-pen" width={16} className="text-[#7C3AED]" />
            <h3 className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3]">{t('proposalSettings.signature.title', 'Assinatura do cliente')}</h3>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(config.assinatura_default?.exigir)}
              onChange={e => setConfig(prev => ({ ...prev, assinatura_default: { ...prev.assinatura_default, exigir: e.target.checked } }))}
              className="w-4 h-4 rounded border-gray-300 accent-[#7C3AED]"
            />
            <span className="text-sm text-gray-700 dark:text-[#D1D2D3]">{t('proposalSettings.signature.requireLabel', 'Exigir assinatura por padrão nas novas propostas')}</span>
          </label>

          <div className="mt-4">
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">{t('proposalSettings.signature.termLabel', 'Termo de aceite padrão')}</Label>
            <textarea
              value={config.assinatura_default?.texto_termo || ''}
              onChange={e => setConfig(prev => ({ ...prev, assinatura_default: { ...prev.assinatura_default, texto_termo: e.target.value } }))}
              rows={2}
              className="mt-1.5 w-full px-3 py-2 text-sm border border-gray-200 dark:border-[#383838] rounded-xl focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]/20 focus:outline-none resize-none dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-3">
            {[
              { chave: 'exigir_documento', label: t('proposalSettings.signature.requireDocumentLabel', 'Pedir CPF ou CNPJ'), ativo: config.assinatura_default?.exigir_documento !== false },
              { chave: 'exigir_email', label: t('proposalSettings.signature.requireEmailLabel', 'Pedir e-mail'), ativo: Boolean(config.assinatura_default?.exigir_email) }
            ].map(item => (
              <label key={item.chave} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.ativo}
                  onChange={e => setConfig(prev => ({ ...prev, assinatura_default: { ...prev.assinatura_default, [item.chave]: e.target.checked } }))}
                  className="w-4 h-4 rounded border-gray-300 accent-[#7C3AED]"
                />
                <span className="text-sm text-gray-700 dark:text-[#D1D2D3]">{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        <Separator className="bg-gray-200/60 dark:bg-[#383838]/60" />

        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">{t('proposalSettings.whatsapp.messageLabel', 'Mensagem padrão do WhatsApp')}</Label>
          <textarea
            value={config.mensagem_whatsapp_padrao || ''}
            onChange={e => setConfig(prev => ({ ...prev, mensagem_whatsapp_padrao: e.target.value }))}
            rows={2}
            className="mt-1.5 w-full px-3 py-2 text-sm border border-gray-200 dark:border-[#383838] rounded-xl focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]/20 focus:outline-none resize-none dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            <Trans
              t={t}
              i18nKey="proposalSettings.whatsapp.linkHint"
              defaults="Use <code1></code1> para inserir o link da proposta."
              components={{ code1: <code className="bg-gray-100 dark:bg-[#1A1D21] px-1 rounded">{'{{link}}'}</code> }}
            />
          </p>
        </div>

      </div>

      {/* E-mail de envio: editor à esquerda, pré-visualização à direita. O
          preview replica o template real montado em propostas.controller.js
          (enviarEmailProposta) — ao mexer num, ajustar o outro. */}
      <div className={`${D.card} mt-4`}>
        <div className={D.cardHeader}>
          <Iconify icon="mdi:email-outline" width={16} className={D.cardIcon} />
          <h3 className={D.cardTitle}>{t('proposalSettings.email.title', 'E-mail de envio')}</h3>
        </div>
        <p className={D.cardDescricao}>
          {t('proposalSettings.email.description', 'Assunto e texto usados quando a proposta é enviada por e-mail. O botão, o link e o rodapé com os dados da empresa são adicionados automaticamente.')}
        </p>

        <div className={D.emailGrid}>
          <div className={D.emailForm}>
            <div>
              <Label className={D.label}>{t('proposalSettings.email.subjectLabel', 'Assunto')}</Label>
              <Input
                ref={assuntoRef}
                value={assunto}
                onChange={e => setEmail({ assunto_padrao: e.target.value })}
                onFocus={() => setCampoAtivo('assunto')}
                placeholder={t('proposalSettings.email.subjectPlaceholder', '{{varEmpresa}} - Proposta comercial: {{varTitulo}}', { varEmpresa: '{{empresa.nome}}', varTitulo: '{{proposta.titulo}}' })}
                className={D.input}
              />
              {assuntoSemEmpresa && (
                <div className={D.sugestao}>
                  <Iconify icon="mdi:lightbulb-outline" width={13} className="text-amber-500 flex-shrink-0 mt-px" />
                  <p className={D.sugestaoTexto}>
                    {t('proposalSettings.email.subjectSuggestionText', 'O assunto não identifica sua empresa. Como o e-mail sai de um remetente compartilhado, incluir o nome ajuda o cliente a reconhecer a mensagem.')}{' '}
                    <button
                      type="button"
                      onClick={() => setEmail({ assunto_padrao: `{{empresa.nome}} - ${assunto}` })}
                      className={D.sugestaoBotao}
                    >
                      {t('proposalSettings.email.subjectSuggestionButton', 'Incluir nome da empresa')}
                    </button>
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label className={D.label}>{t('proposalSettings.email.bodyLabel', 'Corpo')}</Label>
              <textarea
                ref={corpoRef}
                value={corpo}
                onChange={e => setEmail({ corpo_padrao: e.target.value })}
                onFocus={() => setCampoAtivo('corpo')}
                rows={6}
                placeholder={t('proposalSettings.email.bodyPlaceholder', 'Olá {{varContato}}, segue nossa proposta comercial.', { varContato: '{{contato.nome}}' })}
                className={D.textarea}
              />
              <p className={D.ajuda}>{t('proposalSettings.email.bodyHint', 'As quebras de linha são mantidas no e-mail.')}</p>
            </div>

            <div className={D.variaveisBox}>
              <p className={D.variaveisTitulo}>
                <Iconify icon="mdi:code-braces" width={13} className="text-[#7C3AED]" />
                {t('proposalSettings.email.variablesTitle', 'Variáveis disponíveis')}
              </p>
              <p className={D.variaveisAjuda}>
                <Trans
                  t={t}
                  i18nKey="proposalSettings.email.variablesHelp"
                  defaults="Clique para inserir no campo <strong1></strong1>. Cada variável é trocada pelo dado real no envio."
                  components={{ strong1: <strong>{campoAtivo === 'assunto' ? t('proposalSettings.email.subjectLabel', 'Assunto') : t('proposalSettings.email.bodyLabel', 'Corpo')}</strong> }}
                />
              </p>
              {variaveisAgrupadas.map(([grupo, itens]) => (
                <div key={grupo} className={D.variaveisGrupo}>
                  <p className={D.variaveisGrupoNome}>{grupoLabels[grupo] || grupo}</p>
                  <div className={D.variaveisLista}>
                    {itens.map(v => (
                      <button
                        key={v.chave}
                        type="button"
                        // Sem o preventDefault o campo perde o foco no mousedown e
                        // o caret volta para o começo — a variável cairia no lugar errado.
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => inserirVariavel(v.chave)}
                        title={v.label}
                        className={D.variavelChip}
                      >
                        {`{{${v.chave}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={D.previewWrapper}>
            <Label className={D.label}>{t('proposalSettings.email.previewLabel', 'Pré-visualização')}</Label>
            <div className={`${D.previewJanela} mt-1.5`}>
              <div className={D.previewBarra}>
                <div className={D.previewLinha}>
                  <span className={D.previewLinhaLabel}>{t('proposalSettings.email.previewFromLabel', 'De')}</span>
                  <span className={D.previewLinhaValor}>LAIS &lt;contato@lais.app&gt;</span>
                </div>
                <div className={D.previewLinha}>
                  <span className={D.previewLinhaLabel}>{t('proposalSettings.email.previewToLabel', 'Para')}</span>
                  <span className={D.previewLinhaValor}>{dadosPreview['contato.email']}</span>
                </div>
                <p className={`${D.previewAssunto} pt-1`}>{assuntoPreview}</p>
              </div>

              <div className={D.previewCorpoFundo}>
                <div className={D.previewCartao}>
                  <p className={D.previewEmpresa}>{empresaPreview}</p>
                  <p className={D.previewTexto}>{corpoPreview}</p>
                  <span className={D.previewBotao}>{t('proposalSettings.email.previewButton', 'Ver proposta')}</span>
                  <p className={D.previewLinkFallback}>
                    {t('proposalSettings.email.previewLinkFallback', 'Se o botão não funcionar, copie e cole este link no navegador: link.lais.app/propostas/…')}
                  </p>
                  <p className={D.previewRodape}>
                    {empresaPreview}<br />
                    {t('proposalSettings.email.previewFooterText', 'Você recebeu este e-mail porque {{empresa}} enviou uma proposta comercial para este endereço.', { empresa: empresaPreview })}
                  </p>
                </div>
              </div>
            </div>
            <p className={D.previewNota}>
              <Iconify icon="mdi:information-outline" width={12} className="flex-shrink-0 mt-px" />
              {t('proposalSettings.email.previewNote', 'Dados fictícios apenas para visualização.')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white rounded-xl shadow-sm shadow-[#7C3AED]/20"
        >
          {saving && <Iconify icon="mdi:loading" className="mr-2 animate-spin" width={16} />}
          {t('proposalSettings.actions.saveButton', 'Salvar configuração')}
        </Button>
      </div>
    </div>
  );
}
